import React, { useState } from 'react';
import { FaWater, FaMapMarkerAlt, FaSearch, FaThermometerHalf, FaWind, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaInfoCircle, FaCompass } from 'react-icons/fa';
import { getScubaConditions, geocodeLocation } from '../../api/client';

const ScubaDivePredictor = () => {
  const [loading, setLoading] = useState(false);
  const [conditions, setConditions] = useState(null);
  const [error, setError] = useState('');

  // Search inputs
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [locationName, setLocationName] = useState('');

  const handleSearch = async () => {
    if (!searchCity.trim()) {
      setError('Please enter a city or dive site name');
      return;
    }

    setLoading(true);
    setError('');
    setConditions(null);

    try {
      // First geocode the location
      const geoData = await geocodeLocation(searchCity.trim(), searchState.trim() || null, searchCountry.trim() || null);

      if (!geoData.results || geoData.results.length === 0) {
        setError('Location not found. Try a different search term.');
        setLoading(false);
        return;
      }

      const location = geoData.results[0];
      const name = locationName.trim() || `${searchCity}${searchState ? ', ' + searchState : ''}`;

      // Get scuba conditions
      const data = await getScubaConditions(location.lat, location.lng, name);
      setConditions(data);
    } catch (err) {
      console.error('Error fetching conditions:', err);
      setError('Failed to fetch diving conditions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-emerald-500';
    if (score >= 6) return 'text-green-500';
    if (score >= 4) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getRatingBg = (rating) => {
    switch (rating) {
      case 'Excellent': return 'bg-emerald-500';
      case 'Good': return 'bg-green-500';
      case 'Fair': return 'bg-yellow-500';
      default: return 'bg-red-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Bubble animation */}
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/10 animate-pulse"
              style={{
                width: `${Math.random() * 20 + 5}px`,
                height: `${Math.random() * 20 + 5}px`,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 2 + 2}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaWater className="text-2xl text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Scuba Dive Condition Predictor</h2>
              <p className="text-white/70">Check visibility, tides & water conditions</p>
            </div>
          </div>

          <p className="text-white/80 mt-4">
            Enter a coastal location to get real-time diving conditions including water temperature,
            visibility estimates, wave conditions, and tide information.
          </p>
        </div>
      </div>

      {/* Search Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
          <FaMapMarkerAlt className="text-cyan-500" />
          Find Your Dive Site
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              City / Dive Site *
            </label>
            <input
              type="text"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              placeholder="e.g., Cairns, Cozumel"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              State / Region
            </label>
            <input
              type="text"
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              placeholder="e.g., Queensland, Florida"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Country
            </label>
            <input
              type="text"
              value={searchCountry}
              onChange={(e) => setSearchCountry(e.target.value)}
              placeholder="e.g., Australia, Mexico"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Dive Site Name (optional)
            </label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g., Great Barrier Reef"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <FaSearch />
          )}
          {loading ? 'Checking Conditions...' : 'Check Dive Conditions'}
        </button>

        {error && (
          <p className="text-red-600 text-sm mt-3">{error}</p>
        )}
      </div>

      {/* Results */}
      {conditions && (
        <div className="space-y-6 animate-fade-in">
          {/* Main Verdict Card */}
          <div className={`${getRatingBg(conditions.overall_rating)} rounded-2xl p-6 text-white`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  {conditions.should_dive ? (
                    <FaCheckCircle className="text-3xl" />
                  ) : (
                    <FaTimesCircle className="text-3xl" />
                  )}
                  <h3 className="text-2xl font-bold">{conditions.overall_rating} Diving Conditions</h3>
                </div>
                <p className="text-white/90">{conditions.location_name}</p>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold">{conditions.overall_score}/10</div>
                <p className="text-white/80 text-sm">Overall Score</p>
              </div>
            </div>
          </div>

          {/* Conditions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Water Temperature */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaThermometerHalf className="text-cyan-500" />
                <h4 className="font-semibold text-neutral-800">Water Temperature</h4>
              </div>
              <div className="text-3xl font-bold text-neutral-800 mb-1">
                {conditions.water_temp_c}°C <span className="text-lg text-neutral-500">({conditions.water_temp_f}°F)</span>
              </div>
            </div>

            {/* Visibility */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaWater className="text-blue-500" />
                <h4 className="font-semibold text-neutral-800">Visibility</h4>
              </div>
              <div className={`text-2xl font-bold ${getScoreColor(conditions.visibility_score)}`}>
                {conditions.visibility_rating}
              </div>
              <p className="text-neutral-500 text-sm">Score: {conditions.visibility_score}/10</p>
            </div>

            {/* Surface Conditions */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaWater className="text-cyan-500" />
                <h4 className="font-semibold text-neutral-800">Surface Conditions</h4>
              </div>
              <div className="text-lg font-semibold text-neutral-700">{conditions.surface_conditions}</div>
              <p className="text-neutral-500 text-sm">Waves: {conditions.wave_height_m}m, {conditions.wave_period_s}s period</p>
            </div>

            {/* Wind */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaWind className="text-gray-500" />
                <h4 className="font-semibold text-neutral-800">Wind</h4>
              </div>
              <div className="text-2xl font-bold text-neutral-700">{conditions.wind_speed_kmh} km/h</div>
              <p className="text-neutral-500 text-sm">Direction: {conditions.wind_direction}</p>
            </div>

            {/* Tide */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaCompass className="text-indigo-500" />
                <h4 className="font-semibold text-neutral-800">Tide Status</h4>
              </div>
              <div className="text-xl font-bold text-neutral-700">{conditions.tide_status}</div>
              {conditions.next_high_tide && (
                <p className="text-neutral-500 text-sm">Next high: {conditions.next_high_tide}</p>
              )}
            </div>

            {/* Weather */}
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <FaInfoCircle className="text-yellow-500" />
                <h4 className="font-semibold text-neutral-800">Weather</h4>
              </div>
              <div className="text-lg font-semibold text-neutral-700">{conditions.weather_condition}</div>
              <p className="text-neutral-500 text-sm">Best window: {conditions.best_dive_window}</p>
            </div>
          </div>

          {/* Recommendations & Warnings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recommendations */}
            <div className="bg-cyan-50 rounded-2xl p-5 border border-cyan-100">
              <h4 className="font-semibold text-cyan-900 mb-3 flex items-center gap-2">
                <FaCheckCircle className="text-cyan-500" />
                Recommendations
              </h4>
              <ul className="space-y-2">
                {conditions.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-cyan-800 text-sm flex items-start gap-2">
                    <span className="text-cyan-500 mt-1">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>

            {/* Warnings */}
            {conditions.warnings && conditions.warnings.length > 0 && (
              <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <FaExclamationTriangle className="text-amber-500" />
                  Warnings
                </h4>
                <ul className="space-y-2">
                  {conditions.warnings.map((warning, idx) => (
                    <li key={idx} className="text-amber-800 text-sm flex items-start gap-2">
                      <span className="text-amber-500 mt-1">•</span>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-cyan-50 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-cyan-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-cyan-900 mb-1">About This Tool</h4>
          <p className="text-sm text-cyan-700">
            This tool analyzes weather, wave, and marine conditions to help you plan your dive.
            Always check with local dive operators for the most accurate site-specific conditions
            and follow safe diving practices.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScubaDivePredictor;
